<template>
  <div ref="mapContainer" class="mobileAllStoresMap"></div>
</template>

<script>
import { onMounted, onBeforeUnmount, ref, watch } from "vue";
import positionIcon from "@/assets/position.png";
import positionOtherIcon from "@/assets/position_other.png";
import { MAP_REGION_CN, MAP_REGION_MY, getMapRegionDefaultLocation } from "@/utils/config";
import { hasValidMapCoordinates } from "@/views/home/maptiler/useMapTilerAllStores";
import { loadTiandituApi } from "@/views/home/maptiler/useTiandituScript";
import { attachTiandituHtmlMarker } from "@/views/home/maptiler/tiandituHtmlMarker";
import {
  CN_OVERVIEW_BOUNDS,
  MY_OVERVIEW_BOUNDS,
  fitTiandituMapOverview,
  getTiandituDealerFocusZoom,
  getTiandituRegionOverviewFallbackZoom,
} from "@/views/home/maptiler/mapOverviewUtils";
import {
  createDealerMarkerElement,
  getDealerCoordKey,
  readDealerLatLng,
} from "@/views/home/maptiler/mapDealerMarkerUtils";
import { createMapRegionRefreshScheduler } from "@/views/home/maptiler/mapRegionRefreshScheduler";

export default {
  name: "TianDiTuMobileAllStoresMap",
  props: {
    dealers: { type: Array, default: () => [] },
    mapRegion: { type: String, default: MAP_REGION_CN },
    activeKey: { type: [String, Number], default: null },
    bottomInset: { type: Number, default: 120 },
  },
  emits: ["marker-click", "map-ready"],
  setup(props, { emit, expose }) {
    const mapContainer = ref(null);
    const map = ref(null);
    const htmlMarkers = [];
    const locationMarkers = new Map();
    let activeMarkerEntry = null;
    let initToken = 0;

    const clearDealerMarkers = () => {
      htmlMarkers.forEach((marker) => marker.remove());
      htmlMarkers.splice(0, htmlMarkers.length);
      locationMarkers.clear();
      activeMarkerEntry = null;
    };

    const collectOverviewPoints = () => {
      const points = [];
      (props.dealers || []).forEach((element) => {
        if (!hasValidMapCoordinates(element)) return;
        points.push(
          new window.T.LngLat(
            Number(element.longitude ?? element.lng),
            Number(element.latitude ?? element.lat)
          )
        );
      });
      if (props.mapRegion === MAP_REGION_CN) {
        points.push(
          new window.T.LngLat(CN_OVERVIEW_BOUNDS.sw[0], CN_OVERVIEW_BOUNDS.sw[1]),
          new window.T.LngLat(CN_OVERVIEW_BOUNDS.ne[0], CN_OVERVIEW_BOUNDS.ne[1])
        );
      }
      if (props.mapRegion === MAP_REGION_MY) {
        points.push(
          new window.T.LngLat(MY_OVERVIEW_BOUNDS.sw[0], MY_OVERVIEW_BOUNDS.sw[1]),
          new window.T.LngLat(MY_OVERVIEW_BOUNDS.ne[0], MY_OVERVIEW_BOUNDS.ne[1])
        );
      }
      return points;
    };

    const fitAllDealersBounds = () => {
      if (!map.value || !window.T) return;
      fitTiandituMapOverview(map.value, {
        points: collectOverviewPoints(),
        mapRegion: props.mapRegion,
        fallback: getMapRegionDefaultLocation(props.mapRegion),
      });
    };

    const clearActiveDealerHighlight = () => {
      if (activeMarkerEntry) {
        activeMarkerEntry.setActive(false);
        activeMarkerEntry.marker?.resetZIndex?.();
        activeMarkerEntry = null;
      }
    };

    const setLocationActive = (lngNum, latNum, active) => {
      const key = getDealerCoordKey(lngNum, latNum);
      const entry = locationMarkers.get(key);
      if (!entry) return;

      if (active) {
        if (activeMarkerEntry && activeMarkerEntry !== entry) {
          activeMarkerEntry.setActive(false);
          activeMarkerEntry.marker?.resetZIndex?.();
        }
        entry.setActive(true);
        entry.marker?.bringToFront?.();
        activeMarkerEntry = entry;
        return;
      }

      entry.setActive(false);
      entry.marker?.resetZIndex?.();
      if (activeMarkerEntry === entry) {
        activeMarkerEntry = null;
      }
    };

    const resetOverview = () => {
      clearActiveDealerHighlight();
      fitAllDealersBounds();
    };

    const emitMarkerClick = (pos) => {
      const latNum = Number(pos.lat ?? pos.endLatitude);
      const lngNum = Number(pos.lng ?? pos.endLongitude);
      if (Number.isFinite(latNum) && Number.isFinite(lngNum)) {
        setLocationActive(lngNum, latNum, true);
      }
      emit("marker-click", {
        id: "dealer",
        fullName: pos.fullName,
        address: pos.address,
        endLatitude: pos.endLatitude,
        endLongitude: pos.endLongitude,
        groupDealers: pos.groupDealers || [],
      });
    };

    const renderDealerMarkers = () => {
      if (!map.value || !window.T) return;
      clearDealerMarkers();

      const grouped = new Map();
      (props.dealers || []).forEach((element) => {
        if (!hasValidMapCoordinates(element)) return;
        const coords = readDealerLatLng(element);
        if (!coords) return;
        const { lat: latNum, lng: lngNum } = coords;
        const key = getDealerCoordKey(lngNum, latNum);
        if (!grouped.has(key)) {
          grouped.set(key, { latNum, lngNum, dealers: [] });
        }
        grouped.get(key).dealers.push(element);
      });

      grouped.forEach((group) => {
        const first = group.dealers[0];
        const count = group.dealers.length;
        const pos = {
          lat: group.latNum,
          lng: group.lngNum,
          fullName: first?.fullName,
          address: first?.address || first?.fullAddress,
          endLatitude: group.latNum,
          endLongitude: group.lngNum,
          groupDealers: group.dealers,
        };

        const point = new window.T.LngLat(group.lngNum, group.latNum);
        const { root, setActive } = createDealerMarkerElement({
          defaultIconUrl: positionOtherIcon,
          activeIconUrl: positionIcon,
          count,
          onClick: () => emitMarkerClick(pos),
        });
        const marker = attachTiandituHtmlMarker(map.value, point, root, {
          anchor: "bottom",
        });
        htmlMarkers.push(marker);
        locationMarkers.set(getDealerCoordKey(group.lngNum, group.latNum), {
          marker,
          setActive,
        });
      });

      if ((props.dealers || []).length > 0) {
        fitAllDealersBounds();
      }
      requestAnimationFrame(() => {
        htmlMarkers.forEach((item) => item._overlay?.update?.());
      });
    };

    const focusDealer = ({ longitude, latitude }) => {
      if (
        !map.value ||
        !window.T ||
        !hasValidMapCoordinates({ latitude, longitude })
      ) {
        return;
      }
      const lngNum = Number(longitude);
      const latNum = Number(latitude);
      map.value.centerAndZoom(
        new window.T.LngLat(lngNum, latNum),
        getTiandituDealerFocusZoom(props.mapRegion)
      );
      setLocationActive(lngNum, latNum, true);
    };

    const destroyMap = () => {
      clearDealerMarkers();
      if (map.value) {
        try {
          map.value.clearOverLays?.();
        } catch (e) {}
        map.value = null;
      }
    };

    const initMap = async () => {
      if (!mapContainer.value) return;
      const token = ++initToken;
      destroyMap();
      try {
        await loadTiandituApi("zh_cn");
      } catch (e) {
        return;
      }
      if (token !== initToken || !mapContainer.value) return;

      const fallback = getMapRegionDefaultLocation(props.mapRegion);
      map.value = new window.T.Map(mapContainer.value);
      if (!map.value) return;
      map.value.centerAndZoom(
        new window.T.LngLat(fallback.lng, fallback.lat),
        getTiandituRegionOverviewFallbackZoom(props.mapRegion)
      );
      map.value.enableDrag?.();
      let rendered = false;
      const scheduleRender = () => {
        if (rendered || token !== initToken) return;
        rendered = true;
        renderDealerMarkers();
        emit("map-ready", true);
      };
      map.value.addEventListener?.("load", scheduleRender);
      setTimeout(scheduleRender, 120);
    };

    const refreshMapForRegion = () => {
      if (!map.value || !window.T) return;
      if (!(props.dealers || []).length) {
        clearDealerMarkers();
        return;
      }
      renderDealerMarkers();
    };

    const scheduleRegionRefresh = createMapRegionRefreshScheduler(
      refreshMapForRegion
    );

    watch(() => props.mapRegion, scheduleRegionRefresh);
    watch(() => props.dealers, scheduleRegionRefresh, { deep: true });

    onMounted(() => initMap());
    onBeforeUnmount(() => destroyMap());

    expose({ focusDealer, resetOverview });

    return { mapContainer };
  },
};
</script>

<style lang="scss" scoped>
.mobileAllStoresMap {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}
</style>
